<template>
  <v-dialog v-model="dialog" persistent max-width="1200px" scrollable>
    <v-card class="customer-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Edit Customer</div>
          <div class="dialog-subtitle">
            Update customer profile, address, terms, and sales settings.
          </div>
        </div>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body pa-4">
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="form-section">
              <div class="section-title">Name & Numbers</div>

              <div class="field-label mt-3">Name</div>
                <v-text-field
                  autofocus
                  outlined
                  dense
                  v-model="customer.name"
                  label="Name"
                  :error-messages="nameErrors"
                  :hide-details="nameErrors.length === 0"
                  @change="customer.name = properCase(customer.name)"
                  @focus="$event.target.select()"
                />

              <div class="field-label mt-3">Contact Name</div>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="customer.contactName"
                  label="Contact Name"
                  @change="
                    customer.contactName = properCase(customer.contactName)
                  "
                  @focus="$event.target.select()"
                />

              <div class="field-label mt-3">Balance Limit</div>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  class="right-input"
                  @focus="$event.target.select()"
                  v-model="customer.balanceLimit"
                  label="Balance Limit"
                />

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">Cell Number</div>
                  <v-text-field outlined dense hide-details
                    v-model="customer.cellNumber" label="Cell Number"
                    prepend-inner-icon="mdi-cellphone"
                    @focus="$event.target.select()" />
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">Phone Number</div>
                  <v-text-field outlined dense hide-details
                    v-model="customer.phoneNumber" label="Phone Number"
                    prepend-inner-icon="mdi-phone-outline"
                    @focus="$event.target.select()" />
                </v-col>
              </v-row>

              <div class="field-label mt-3">Email Address</div>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="customer.email"
                  label="Email Address"
                  prepend-inner-icon="mdi-email-outline"
                />

              <div class="field-label mt-3">Tax ID</div>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="customer.taxId"
                  label="Tax ID"
                />

              <div class="field-label mt-3">Customer Terms</div>
                <DayPicker
                  v-model="customer.metadata.invoiceDue"
                  :initValue="customer.metadata.invoiceDue"
                  :title="'Customer Terms'"
                />
            </div>
          </v-col>

          <v-col cols="12" md="4">
            <div class="form-section">
              <div class="section-title">Billing Address</div>
              <div class="field-label mt-3">Search Billing Address</div>
              <GoogleAddressInput label="Search Billing Address"
                @address-selected="setBillingAddress" />

              <div class="field-label mt-3">Address</div>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="customer.address"
                  label="Address"
                />

              <div class="field-label mt-3">City</div>
                <v-text-field
                  outlined
                  dense
                  hide-details
                  @change="customer.city = properCase(customer.city)"
                  v-model="customer.city"
                  label="City"
                />

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">State</div>
                  <v-text-field outlined dense hide-details
                    v-model="customer.state" label="State" />
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">Zip Code</div>
                  <v-text-field outlined dense hide-details
                    v-model="customer.zip" label="Zip Code" />
                </v-col>
              </v-row>
            </div>

            <div class="form-section mt-4">
              <div class="section-title-row">
                <div class="section-title">Shipping Address</div>
                <v-btn v-if="customer.address" x-small rounded outlined
                  @click="copyBillingAddressToShipping">
                  Same as Billing
                </v-btn>
              </div>

              <div class="field-label mt-3">Search Shipping Address</div>
              <GoogleAddressInput label="Search Shipping Address"
                @address-selected="setShippingAddress" />

              <div class="field-label mt-3">Address</div>
              <v-text-field outlined dense hide-details
                v-model="customer.shippingAddress" label="Address" />

              <div class="field-label mt-3">City</div>
              <v-text-field outlined dense hide-details
                v-model="customer.shippingCity" label="City"
                @change="customer.shippingCity = properCase(customer.shippingCity)" />

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">State</div>
                  <v-text-field outlined dense hide-details
                    v-model="customer.shippingState" label="State" />
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">Zip Code</div>
                  <v-text-field outlined dense hide-details
                    v-model="customer.shippingZip" label="Zip Code" />
                </v-col>
              </v-row>
            </div>
          </v-col>

          <v-col cols="12" md="4">
            <div class="form-section">
              <div class="section-title">Other</div>

              <div class="field-label mt-3">Sales Rep</div>
              <SalesMan
                v-model="selectedUser"
                :rounded="false"
                :initValue="customer.SalesRep"
              />

              <div v-if="currentBusiness && currentBusiness.metadata && !currentBusiness.metadata.isSingleCustomerPricing">
                <div class="field-label mt-3">Price Tier</div>
              <v-select
                :items="tiers"
                item-text="name"
                item-value="id"
                clearable
                v-model="customer.PriceTierId"
                label="Price Tier"
                outlined
                dense
                hide-details
              />
              </div>

              <div class="field-label mt-3">Note</div>
              <v-textarea outlined dense hide-details auto-grow rows="5"
                v-model="customer.note" :label="$t('labels.note')" />

              <div class="setting-box mt-4">
                <div>
                  <div class="setting-title">Show Balance on Invoice</div>
                  <div class="setting-subtitle">
                    Show or hide remaining balance on printed and viewed invoices.
                  </div>
                </div>
                <toggle-button :width="60"
                  :labels="{ checked: 'Show', unchecked: 'Hide' }"
                  v-model="customer.showBalance"
                  :value="customer.showBalance" :sync="true" />
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />
      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn color="primary" text @click="close">Close</v-btn>
        <v-btn color="primary" rounded dark :loading="loading" @click="submit">
          Update Customer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import customerService from "../service.js";
import GlobalFunction from "@/mixins/GlobalFunction";
import SalesMan from "@/modules/User/components/AutoComplete.vue";
import GoogleAddressInput from "@/modules/Google/GoogleAddressInput.vue";
import DayPicker from "@/components/DayPicker";
import _ from "lodash";
import priceTierService from "@/modules/Product/PriceTier/service.js";
const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  props: {
    editDialog: {
      default: false,
    },
    editCustomer: {
      type: Object,
    },
  },
  name: "update-customer",
  data() {
    return {
      selectedTab: 0,
      loading: false,
      dialog: false,
      tiers: [],
      selectedUser: null,
      customer: {
        showBalance: true,
        name: null,
        cellNumber: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        shippingAddress: null,
        shippingCity: null,
        shippingState: null,
        shippingZip: null,
        note: null,
        SalesRepUserId: null,
        metadata: {
          invoiceDue: null,
        },
      },
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    customer: {
      name: {
        required,
      },
    },
  },
  watch: {
    editDialog(val) {
      this.customer = _.clone(this.editCustomer);
      if (!this.customer.metadata) {
        this.customer.metadata = {
          invoiceDue: null,
        };
      } else if (!this.customer.metadata.hasOwnProperty("invoiceDue")) {
        this.customer.metadata.invoiceDue = null;
      }
      this.dialog = val;
      if (val && this.currentBusiness && this.currentBusiness.metadata && !this.currentBusiness.metadata.isSingleCustomerPricing) {
        this.getTiers();
      }
    },
    selectedUser(val) {
      this.customer.SalesRepUserId = val.id;
    },
  },
  components: {
    SalesMan,
    DayPicker,
    GoogleAddressInput,
  },
  computed: {
    ...mapGetters("global", ["currentUser", "currentBusiness"]),
    nameErrors() {
      const errors = [];
      if (!this.$v.customer.name.$dirty) return errors;
      if (!this.$v.customer.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));

      return errors;
    },
  },
  methods: {
    setBillingAddress(addressData) {
      this.customer.address = addressData.address;
      this.customer.city = addressData.city;
      this.customer.state = addressData.state;
      this.customer.zip = addressData.zip;
    },
    setShippingAddress(addressData) {
      this.customer.shippingAddress = addressData.address;
      this.customer.shippingCity = addressData.city;
      this.customer.shippingState = addressData.state;
      this.customer.shippingZip = addressData.zip;
    },
    async getTiers() {
      try {
        const response = await priceTierService.getAll();
        this.tiers = response.data;
      } catch (e) {
        console.error("Failed to load tiers", e);
      }
    },
    copyBillingAddressToShipping() {
      this.customer.shippingAddress = this.customer.address;
      this.customer.shippingCity = this.customer.city;
      this.customer.shippingState = this.customer.state;
      this.customer.shippingZip = this.customer.zip;
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.selectedTab = 0;
      this.customer = {
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        shippingAddress: null,
        shippingCity: null,
        shippingState: null,
        shippingZip: null,
        note: null,
        metadata: {
          invoiceDue: null,
        },
      };
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        return customerService
          .update(this.customer.id, this.customer)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Customer is updated!",
                });
              this.loading = false;
              this.$emit("submit");
              this.$events.emit("customerListEvent");

              this.close();
            }
          })
          .catch((err) => {
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
          });
      }
    },
  },
};
</script>

<style scoped>
.customer-dialog-card {
  border-radius: 20px;
  overflow: hidden;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 0 0 auto;
  background: #ffffff;
}

.dialog-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.dialog-actions {
  padding: 12px 16px;
  flex: 0 0 auto;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
}

.section-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.setting-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.setting-title {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
}

.setting-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.right-input input {
  text-align: right;
}
</style>
